import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { SectionHeader } from "components/ui/section-header"
import type { WidgetProps } from "lib/widget-types"
import { ChevronRight } from "lucide-react"
import { getTasks } from "services/tasks"

interface TaskPriority {
  id: string
  label: string
  count: number
  priority?: "high" | "expiring" | "overdue"
  subtitle?: string
}

const priorityConfig = {
  high: {
    badge: "Alta",
    bgColor: "var(--hexa-caution-light)",
    textColor: "var(--hexa-caution)",
  },
  expiring: {
    badge: "In scadenza",
    bgColor: "var(--hexa-warning-light)",
    textColor: "var(--hexa-warning)",
  },
  overdue: {
    badge: "Scaduti",
    bgColor: "var(--hexa-error-light)",
    textColor: "var(--hexa-error)",
  },
}

export function TaskStatusWidget({ widget }: WidgetProps) {
  const [priorities, setPriorities] = useState<TaskPriority[]>([
    { id: "high", label: "Alta", count: 0, priority: "high", subtitle: "Priorità alta" },
    { id: "expiring", label: "In scadenza", count: 0, priority: "expiring", subtitle: "Entro 48 ore" },
    { id: "overdue", label: "Scaduti", count: 0, priority: "overdue", subtitle: "Richiede attenzione" },
  ])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchTaskPriorities = async () => {
      try {
        const response = await getTasks()
        // L'endpoint è paginato: la response è { count, next, previous, results }.
        // Senza unwrap, Array.isArray(response.data) è false → tasks=[] → conteggi sempre 0 (bug B6).
        const tasks = Array.isArray(response.data) ? response.data : (response.data?.results ?? [])

        // Count tasks by priority (excluding completed ones).
        // Le key di TaskStatus sono minuscole ("completed"); il flag booleano è is_completed.
        const activeTasks = tasks.filter((t: any) => t.status?.key !== "completed" && !t.is_completed)

        // Alta: priority === 3 o "ALTA" (backend usa numeri)
        const highCount = activeTasks.filter((t: any) =>
          t.priority === 3 || t.priority === "ALTA" || t.priority === "HIGH" || t.priority === "high"
        ).length

        const now = new Date()
        const in48Hours = new Date(now.getTime() + 48 * 60 * 60 * 1000)

        // In scadenza: due_date entro 48h nel futuro, non ancora scaduto
        const expiringCount = activeTasks.filter((t: any) => {
          if (!t.due_date) return false
          const dueDate = new Date(t.due_date)
          return dueDate >= now && dueDate <= in48Hours
        }).length

        // Scaduti: due_date nel passato
        const overdueCount = activeTasks.filter((t: any) => {
          if (!t.due_date) return false
          return new Date(t.due_date) < now
        }).length

        setPriorities([
          { id: "high", label: "Alta", count: highCount, priority: "high", subtitle: "Priorità alta" },
          { id: "expiring", label: "In scadenza", count: expiringCount, priority: "expiring", subtitle: "Entro 48 ore" },
          { id: "overdue", label: "Scaduti", count: overdueCount, priority: "overdue", subtitle: "Richiede attenzione" },
        ])
      } catch (error) {
        console.error("Error fetching task priorities:", error)
        // Keep default zeros on error
      } finally {
        setLoading(false)
      }
    }

    fetchTaskPriorities()
  }, [])
  const limit = (widget.config?.limit as number) || priorities.length
  const showSubtitle = widget.config?.showSubtitle !== false

  const [hoveredId, setHoveredId] = useState<string | null>(null)

  return (
    <div
      className="rounded-xl backdrop-blur-sm p-4 border hexa-card-shadow h-full flex flex-col"
      style={{
        backgroundColor: "var(--hexa-card-bg)",
        borderColor: "var(--hexa-border)",
      }}
    >
      <Link to="/tasks" className="cursor-pointer flex-shrink-0">
        <SectionHeader
          title={widget.title || "Priorità Task"}
          icon={<ChevronRight className="w-4 h-4" style={{ color: "var(--hexa-purple)" }} />}
        />
      </Link>
      <div className="flex-1 min-h-0 flex flex-col gap-1.5 overflow-y-auto pr-1 scrollbar-hexa">
        {priorities.slice(0, limit).map((priority) => {
          const config = priority.priority ? priorityConfig[priority.priority] : null
          const isHovered = hoveredId === priority.id
          return (
            <Link
              key={priority.id}
              to="/tasks"
              className="flex-1 flex items-center w-full p-1.5 rounded-lg border transition-colors group"
              style={{
                backgroundColor: isHovered ? "var(--hexa-sidebar-item-hover)" : "var(--hexa-card-bg)",
                borderColor: "var(--hexa-border-subtle)",
                opacity: isHovered ? 0.9 : 1,
              }}
              onMouseEnter={() => setHoveredId(priority.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              <div className="flex items-center justify-between gap-2.5 w-full">
                <div className="flex-1 min-w-0 text-left">
                  <div className="text-xs font-medium truncate" style={{ color: "var(--hexa-text-primary)" }}>
                    {priority.label}
                  </div>
                  {showSubtitle && priority.subtitle && (
                    <div
                      className="text-[10px] font-normal truncate mt-0"
                      style={{ color: "var(--hexa-text-secondary)" }}
                    >
                      {priority.subtitle}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2.5 flex-shrink-0">
                  <div className="text-base font-semibold tabular" style={{ color: "var(--hexa-text-primary)" }}>
                    {priority.count}
                  </div>
                  {config && (
                    <div
                      className="px-1.5 py-0.5 rounded text-[10px] font-medium"
                      style={{
                        backgroundColor: config.bgColor,
                        color: config.textColor,
                      }}
                    >
                      {config.badge}
                    </div>
                  )}
                  <ChevronRight
                    className="w-3.5 h-3.5 transition-opacity"
                    style={{
                      color: "var(--hexa-purple)",
                      opacity: isHovered ? 1 : 0,
                    }}
                  />
                </div>
              </div>
            </Link>
          )
        })}
      </div>
    </div>
  )
}
